import { useMemo, useCallback, useState } from "react";
import { orderBy } from "lodash";
import { Box, Button } from "@material-ui/core";
import DataListHeader from "components/DataListHeader";
import DataTable from "components/DataTable/DataTable";
import LoadingSpinner from "components/LoadingSpinner";
import PageComponent from "components/Page/PageComponent";
import ActivityTypesField from "features/activity/ActivityTypesField";
import { useGetAllActivityItemsQuery } from "features/activity/activitySlice";
import { useGetLotsQuery } from "features/lots/lotSlice";
import { useGetPricingByActivityTypeQuery } from "features/pricing/pricingSlice";
import { useGetVendorsQuery } from "features/vendor/vendorSlice";
import { useGetClientsQuery } from "features/clients/clientSlice";
import { arrayToObject } from "utils/object-util";
import { fieldTypes } from "services/filterService";
import useSortOptions from "hooks/useSortOptions";
import { PricingByClientStyle } from "../PricingByClient/PricingByClientStyle";
import { IconSearch } from "icons";
import { NO_ITEMS_MESSAGE, } from "../PricingByClient/PricingByClientConst";
import { getPricingCellStyle } from "../PricingByClient/pricingByClientUtils";
import PricingLotCell from "../PricingByClient/Components/PricingLotCell";
import PricingLegend from "../PricingByClient/Components/PricingLegend";
import PricingLotDetailsDialog from "../PricingByClient/Components/PricingLotDetailsDialog/PricingLotDetailsDialog";
import { buildActivityPricingMatrixData } from "./pricingByActivityUtils";
import ActivityPricingManagementDialog, { ACTIVITY_PRICING_DIALOG_MODES } from "../CreateActivityAndPricing/CreateActivityAndPricingDialog";
import BundleDialog, { BUNDLE_DIALOG_MODES } from "../CreateBundle/BundleDialog";
import { Alert } from "@mui/material";
import { ActivityItemPricingType } from "../PricingManagementConsts";

const PricingByActivity = () => {
  const classes = PricingByClientStyle();
  const [sortOptions, sortData] = useSortOptions();
  const [filters, setFilters] = useState({ lotIds: { value: [] } });
  const [selectedPricingCell, setSelectedPricingCell] = useState(null);

  // Edit activity item state
  const [openDialog, setOpenDialog] = useState(false);
  const [activityItemIdToEdit, setActivityItemIdToEdit] = useState(null);

  // Edit bundle state
  const [openBundleEditDialog, setOpenBundleEditDialog] = useState(false);
  const [bundleIdToEdit, setBundleIdToEdit] = useState(null);

  const onDropFilter = (value, prop) => {
    setFilters({ ...filters, [prop]: { value } });
  };

  const onActivityFilterChange = (rawId, { isBundle, jsonValue } = {}) => {
    setSearchParams(null);
    if (isBundle) {
      setFilters((prev) => ({ ...prev, bundleId: { value: rawId }, activityItemId: undefined, activityDropdownValue: jsonValue }));
    } else {
      setFilters((prev) => ({ ...prev, activityItemId: { value: rawId }, bundleId: undefined, activityDropdownValue: jsonValue }));
    }
  };

  const [searchParams, setSearchParams] = useState(null);
  const { data: activityPricing, isFetching: isLoadingActivityPricing } = useGetPricingByActivityTypeQuery(searchParams, { skip: !searchParams });

  let { data: lots, isLoading: isLoadingLots } = useGetLotsQuery();
  let { data: vendors, isLoading: isLoadingVendors } = useGetVendorsQuery();
  let { data: clients, isLoading: isLoadingClients } = useGetClientsQuery(null);
  let { data: activityItems, isLoading: isLoadingActivityItems } = useGetAllActivityItemsQuery();
  activityItems = activityItems || [];

  const vendorLookup = arrayToObject(vendors);
  const clientLookup = useMemo(() => arrayToObject(clients), [clients]);
  const activityItemsLookup = arrayToObject(activityItems || []);
  const lotsLookup = arrayToObject(lots);

  const selectedLotIds = filters?.lotIds?.value || [];

  const executeSearch = () => {
    const activityId = filters?.activityItemId?.value;
    const bundleId = filters?.bundleId?.value;
    setSearchParams({ activityId, bundleId });
  };

  const handleSearch = () => {
    executeSearch();
  };

  // Edit activity item handlers
  const selectedActivityItemId = filters?.activityItemId?.value
    ? Number(filters?.activityItemId?.value)
    : null;
  const selectedActivityItem = activityItemsLookup[selectedActivityItemId];

  const selectedBundleId = filters?.bundleId?.value
    ? Number(filters?.bundleId?.value)
    : null;

  const onEditActivityItem = () => {
    if (!selectedActivityItem) return;
    setActivityItemIdToEdit(selectedActivityItem.id);
    setOpenDialog(true);
  };

  const onEditBundle = () => {
    if (!selectedBundleId) return;
    setBundleIdToEdit(selectedBundleId);
    setOpenBundleEditDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setActivityItemIdToEdit(null);
  };

  const handleBundleEditClose = () => {
    setOpenBundleEditDialog(false);
    setBundleIdToEdit(null);
  };

  // Pricing cell click handlers
  const handlePricingCellClick = (pricingData) => {
    setSelectedPricingCell({ ...pricingData, pricingType: selectedActivityItem?.pricingType ?? ActivityItemPricingType.SIMPLE });
  };

  const handlePricingDialogClose = () => {
    setSelectedPricingCell(null);
  };

  const filterConfig = [
    {
      type: fieldTypes.COMPONENT,
      size: 4,
      component: (
        <ActivityTypesField
          onChange={onActivityFilterChange}
          value={filters?.activityDropdownValue}
          required={true}
          label="Activity"
          includeBundles={true}
        />
      ),
    },
    {
      type: fieldTypes.LOT,
      size: 5,
      title: "Lots",
      key: "lotIds",
      values: lots,
      multiple: true,
    },
    {
      type: fieldTypes.COMPONENT,
      size: 1,
      key: "searchButton",
      component: (
        <Box display="flex" alignItems="flex-end">
          <Button
            color="primary"
            variant="contained"
            onClick={handleSearch}
            disabled={!(filters?.activityItemId?.value || filters?.bundleId?.value)}
            startIcon={<IconSearch fill="white" />}
            style={{ height: "40px", marginTop: "7px" }}
          >
            Search
          </Button>
        </Box>
      ),
    },
  ];

  // Build matrix: clients as rows, lots as columns
  const { rows: matrixData, lotIdsFromData } = useMemo(() => {
    return buildActivityPricingMatrixData({
      activityPricing,
      selectedLotIds,
      clientLookup,
      vendorLookup,
    });
  }, [activityPricing, selectedLotIds, clientLookup, vendorLookup]);

  const sortedMatrixData = useMemo(() => {
    if (!sortOptions.columnToSort) return matrixData;
    return orderBy(
      matrixData,
      (row) => {
        const key = sortOptions.columnToSort;
        if (key.startsWith("lot_")) {
          return row[key]?.charge ?? null;
        }
        return row[key];
      },
      sortOptions.sortDirection
    );
  }, [matrixData, sortOptions]);

  // Derive lot columns: use selected lots if filtered, otherwise from data
  const lotsForColumns = useMemo(() => {
    const lotIdsForColumns = selectedLotIds?.length > 0
      ? Array.from(new Set(selectedLotIds))
      : Array.from(lotIdsFromData);

    return lotIdsForColumns
      .map((id) => lotsLookup[id])
      .filter(Boolean)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [selectedLotIds, lotIdsFromData, lotsLookup]);

  const columns = useMemo(() => {
    return [
      {
        name: "Client",
        sortKey: "clientLabel",
        key: "clientLabel",
        width: 200,
        component: (row) => <div className={classes.activityCell}>{row?.clientLabel}</div>,
      },
      ...lotsForColumns.map((lot) => ({
        name: lot.name,
        sortKey: `lot_${lot.id}`,
        key: `lot_${lot.id}`,
        width: 120,
        cellStyle: (row) =>
          getPricingCellStyle({ row, lotId: lot.id }),
        component: (row) => {
          const pricingData = row[`lot_${lot.id}`];
          return (
            <PricingLotCell
              pricingData={pricingData}
              classes={classes}
              onClick={() => handlePricingCellClick(pricingData)}
            />
          );
        },
      })),
    ];
  }, [lotsForColumns, classes]);

  const mapExportData = useCallback(
    (row) => {
      const exportRow = { Client: row.clientLabel };
      lotsForColumns.forEach((lot) => {
        const cell = row[`lot_${lot.id}`];
        exportRow[lot.name] = cell ? cell.formattedCharge : "-";
      });
      return exportRow;
    },
    [lotsForColumns]
  );

  const isLoading =
    isLoadingActivityItems ||
    isLoadingLots ||
    isLoadingActivityPricing ||
    isLoadingVendors ||
    isLoadingClients;

  const showTable = searchParams !== null;

  return (
    <PageComponent
      header={
        <DataListHeader
          title="Pricing By Activity"
          data={matrixData}
          dropDownsConfig={filterConfig}
          filters={filters}
          onDropFilter={onDropFilter}
          actionButtons={
            selectedBundleId ? (
              <Button
                variant="outlined"
                color="primary"
                onClick={onEditBundle}
              >
                Edit Bundle
              </Button>
            ) : (
              <Button
                variant="outlined"
                color="primary"
                disabled={!selectedActivityItemId}
                onClick={onEditActivityItem}
              >
                Edit Activity Item
              </Button>
            )
          }
        />
      }
    >
      {!showTable ? (
        <div className={classes.emptyStateMessage}>{"Please select an activity to view pricing data"}</div>
      ) : (
        <>
          <LoadingSpinner loading={isLoading} />
          <PricingLegend />
          {!isLoading && (
            <>
              {!selectedLotIds?.length && (
                <Alert severity="warning" className={classes.alert}>
                  {"Note: For better performance, consider filtering by specific lots if you have a large dataset."}
                </Alert>
              )}
              <DataTable
                columns={columns}
                rows={sortedMatrixData}
                rowIdentifier="id"
                onSort={sortData}
                mapExportData={mapExportData}
                sortDirection={sortOptions.sortDirection}
                columnToSort={sortOptions.columnToSort}
                noItemsMessage={NO_ITEMS_MESSAGE}
                headerStyle={{ paddingLeft: "1rem" }}
                isLoading={isLoading}
              />
            </>
          )}
        </>
      )}

      <PricingLotDetailsDialog
        open={!!selectedPricingCell}
        onClose={handlePricingDialogClose}
        pricingData={selectedPricingCell}
      />

      <ActivityPricingManagementDialog
        open={openDialog}
        activityItemId={activityItemIdToEdit}
        handleClose={handleClose}
        mode={ACTIVITY_PRICING_DIALOG_MODES.EDIT}
      />

      <BundleDialog
        open={openBundleEditDialog}
        bundleId={bundleIdToEdit}
        handleClose={handleBundleEditClose}
        mode={BUNDLE_DIALOG_MODES.EDIT}
      />
    </PageComponent>
  );
};

export default PricingByActivity;
